import { Component } from 'substance'
import { isEqual } from '../../../lib/utils/isEqual'

import './scss/toolbox.scss'

/**
 * @typedef UIToggle.state
 * @property {Map} activeToolGroups - Active tool groups in toolgroup
 */

/**
 * Base class for creating toolbox style menus.
 *
 * @name UIToolbox
 * @class
 *
 * @example
 * import {UIToolbox} from 'writer'
 *
 * class AnnotationMenuComponent extends UIToolbox {
 *
 * }
 *
 * @property {UIToggle.state} state
 */
class ToolboxComponent extends Component {
    getInitialState() {
        return {
            trigger: this.props.trigger,
            active: false,
            seq: 0,
            ...this.getActiveToolGroups()
        }
    }

    didMount() {
        this._setupListeners()
    }

    dispose() {
        this._teardownListeners()
    }

    shouldRerender(newProps, newState) {
        return (newState.seq > this.state.seq || this.state.hasActiveTools !== newState.hasActiveTools)

    }

    _setupListeners() {
        // selection:positioned or dom-selection:rendered ?
        this.context.scrollPane.on('dom-selection:rendered', this._onDomSelection, this)
        this.context.scrollPane.on('context-menu:opened', this._onContextOpened, this)
        this.context.editorSession.onRender(
            this._onCommandStatesChanged, this
        )
        document.addEventListener('click', this._onOutsideClick.bind(this))
    }

    _teardownListeners() {
        this.context.scrollPane.off('dom-selection:rendered', this._onDomSelection, this)
        this.context.scrollPane.off('context-menu:opened', this._onContextOpened, this)
        this.context.editorSession.off(this)
        document.removeEventListener('click', this._onOutsideClick.bind(this))
    }

    _onOutsideClick(e) {
        if (this.state.trigger === 'selection' && this.state.open) {
            // Content menu open
        }
        else if (this.state.trigger === 'selection' && this.state.active) {
            // Annotation menu open
        }
        else if (this.state.trigger === 'context' && this.state.active) {
            // Context menu open
        }
        else {
            return
        }

        if (!this.el.el.contains(e.target)) {
            this.extendState({
                active: false,
                open: false,
                seq: this.state.seq + 1
            })
        }
    }

    _onContextOpened(hints) {
        this.onStateChange()

        if (this.props.trigger === 'context') {
            this.extendState({
                hints,
                active: true,
                seq: this.state.seq + 1
            })
        }
        else if (this.props.trigger === 'selection') {
            this.extendState({
                seq: this.state.seq + 1,
                active: false
            })
        }
    }

    _onDomSelection(hints) {
        this.onStateChange()

        if (this.props.trigger === 'context') {
            this.extendState({
                seq: this.state.seq + 1,
                active: false
            })
        }
        else if (this.props.trigger === 'selection') {
            this.extendState({
                hints,
                seq: this.state.seq + 1
            })
        }
    }

    /**
     * Abstract method that if overridden calls sub class, allows sub class
     * to update it's state prior to toolbox super class state
     */
    onStateChange() { // eslint-disable-line
        // Should be implemented in sub class if needed
    }

    _onCommandStatesChanged(editorSession) {
        if (!editorSession.hasChanged('commandStates')) {
            return
        }

        const {activeToolGroups, activeToolGroupsJson, hasActiveTools} = this.getActiveToolGroups()

        this.extendState({
            activeToolGroups,
            activeToolGroupsJson,
            hasActiveTools,
            active: this.shouldBeActive(activeToolGroupsJson)
        })
    }

    shouldBeActive(toolgroups) {
        for (var n in toolgroups) {
            if (toolgroups[n].length) {
                return true
            }
        }

        return false
    }

    hasChanged(newProps, newState) {
        return !isEqual(this.props, newProps) || !isEqual(this.state.activeToolGroups, newState.activeToolGroups)
    }

    /*
      Returns A map of tooltargets, the value being an array of tool objects
      containing Class and toolProps for rendering
    */
    getActiveToolGroups() {
        let toolGroups = this.context.toolGroups
        let activeToolGroupNames = this.getActiveToolGroupNames()
        let activeToolGroups = new Map()
        let hasActiveTools = false

        const sel = this.context.api.editorSession.getSelection()
        if (sel.isNull()) {
            return {
                hasActiveTools: false,
                activeToolGroups: [],
                activeToolGroupsJson: {}
            }
        }

        activeToolGroupNames.forEach((toolGroupName) => {
            let toolGroup = toolGroups.get(toolGroupName)
            if (!toolGroup) {
                return
            }

            let tools = toolGroup.tools
            let { activeTools, active } = this.getActiveTools(tools, toolGroupName, sel)

            if (active === true) {
                hasActiveTools = true
            }

            // Allow toolbox subclass to override decision
            if (this.shouldToolGroupBeEnabled && !this.shouldToolGroupBeEnabled(toolGroupName, sel)) {
                return
            }

            activeToolGroups.set(toolGroupName, {
                name: toolGroupName,
                Class: toolGroup.Class,
                tools: activeTools
            })
        })

        return {
            hasActiveTools,
            activeToolGroups,
            activeToolGroupsJson: this.activeToolGroupsAsJson(activeToolGroups)
        }
    }

    activeToolGroupsAsJson(activeToolGroups) {
        let entries = {}
        activeToolGroups.forEach(group => {
            entries[group.name] = []
            if (group.tools) {
                group.tools.forEach(tool => {
                    entries[group.name].push(tool.name)
                })
            }
        })

        return entries
    }


    /*
      For a given Map of tools and a toolGroupName determine
      a set of activeTools, based on the current commandStates
    */
    getActiveTools(tools, toolGroupName, sel) { // eslint-disable-line
        let activeTools = new Map()
        let commandStates = this._getCommandStates()
        let active = false

        // Allow toolbox subclass to override decision
        if (this.overrideCommandStates) {
            this.overrideCommandStates(toolGroupName, commandStates, sel)
        }

        tools.forEach((tool, toolName) => {
            if (!commandStates[toolName]) {
                throw new Error(`Could not find commandState for ${toolName}`)
            }

            let toolProps = Object.assign({}, commandStates[toolName], {
                name: toolName,
                label: toolName,
                // style hint only interprerted by generic Tool class
                style: this.getToolStyle(toolName)
            })

            if (!toolProps.disabled || this.showDisabled()) {
                activeTools.set(tool.name, {
                    name: tool.name,
                    Class: tool.Class,
                    toolProps: toolProps
                })
                active = true
            }
        })

        return {activeTools, active}
    }

    getActiveToolGroupNames() {
        throw new Error('ToolboxComponent.getActiveToolGroupNames must be implemented in sub class')
    }

    showDisabled() {
        return this.props.trigger === 'context'
    }

    getToolStyle() {
        return 'outline-dark'
    }

    _getCommandStates() {
        return this.context.commandManager.getCommandStates()
    }
}

export {ToolboxComponent}
